import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import moment from 'moment';

import { MainContent, PageHeading } from '../../components/Shell/Content';

import DataTable from '../../components/DataTable';
import LoadingSkeleton from '../../components/Shell/LoadingSkeleton';
import { AvatarGenerated } from '../../components/Avatar';
import Badge from '../../components/Badge';
import {
	capitalizeFirstLetter,
	classNames,
	objectAccess,
} from '../../components/helpers/functions';
import { TAG_COLOR_OPTIONS } from '../../components/helpers/constants';

import { getModuleFields } from '../../api/modules';
import { getAssets, getLocations } from '../../api';

import usePrevious from '../../hooks/usePrevious';
import useSearchParams from '../../hooks/useSearchParams';
import useAsync from '../../hooks/useAsync';

import LocationList from './LocationList';
import useSettings from '../../hooks/useSettings';
import NewDataTableBeta from '../../components/Table';
import {
	CheckIcon,
	ChevronDownIcon,
	ChevronLeftIcon,
	PlusIcon,
	PencilIcon,
	Cog6ToothIcon,
	ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { Transition } from '@headlessui/react';
import {
	createTableView,
	deleteTableView,
	getAllTableViews,
	updateTableView,
} from '../../api/table-views';
import { Combobox } from '@headlessui/react';
import useModal from '../../hooks/useModal';
import { getTags } from '../../api/tags';
import { Menu } from '@headlessui/react';
import { TemplateField } from '../../components/Attributes/Attributes_old';
import Modal from '../../components/Shell/Modal';
import Button from '../../components/Button';
const ASSETS_MODULE_ID = process.env.REACT_APP_MODULE_ID_ASSETS;

const people = [
	{ id: 1, name: 'Tom Cook' },
	{ id: 2, name: 'Wade Cooper' },
	{ id: 3, name: 'Tanya Fox' },
	{ id: 4, name: 'Arlene Mccoy' },
	{ id: 5, name: 'Devon Webb' },
];

const HEALTH_BANNERS = {
	outage: {
		message: (
			<>
				<span className="font-medium">Critical: </span>This location is
				experiencing an outage
			</>
		),
		status: 'error',
	},
	locationOutage: {
		message: (
			<>
				<span className="font-medium">Critical: </span>This location is
				experiencing a location wide outage. Please avoid using any system in
				this location until the issue has been resolved
			</>
		),
		status: 'error',
	},
	partialOutage: {
		message: (
			<>
				<span className="font-medium">Warning: </span>This location is
				experiencing a partial outage. Please check the affected systems before
				using
			</>
		),
		status: 'warning',
	},
	completeOutage: {
		message: (
			<>
				<span className="font-medium">Critical: </span>This location is
				experiencing a complete outage. Please avoid using this system until we
				have resolved the issue
			</>
		),
		status: 'error',
	},
	disruption: {
		message: (
			<>
				<span className="font-medium">Caution: </span>This location is
				experiencing a disruption. Please proceed with caution
			</>
		),
		status: 'caution',
	},
	locationDisruption: {
		message: (
			<>
				<span className="font-medium">Caution: </span>This location is
				experiencing a disruption. Please proceed with caution
			</>
		),
		status: 'caution',
	},
	partialDisruption: {
		message: (
			<>
				<span className="font-medium">Caution: </span>This location is
				experiencing a partial disruption. Please verify that the system you are
				trying to use is not affected
			</>
		),
		status: 'caution',
	},
	completeDisruption: {
		message: (
			<>
				<span className="font-medium">Warning: </span>This location is
				experiencing a complete disruption. Please be cautious when using this
				system until the issues are resolved
			</>
		),
		status: 'warning',
	},
};

const AllList = () => {
	const navigate = useNavigate();
	const locationDOM = useLocation();

	const [selectedLocation, setSelectedLocation] = useState(null);
	const [isRenameTableModalOpen, setRenameTableModalOpen] = useState(false);

	const { setClose: closeDeleteModal, setOpen: openDeleteModal } =
		useModal('deleteModal');

	const { open, setOpen } = useModal('assetModalForm');

	const {
		get: getSettings,
		set: updateSettings,
		loading: isLoadingSettings,
	} = useSettings();

	const {
		query: queryFilter,
		manufacturerId: manufacturerFilter,
		modelId: modelFilter,
		categoryId: categoryFilter,
		locationId: locationFilter,
		sortBy,
		sortOrder,
		page,
	} = useSearchParams();

	const paths = locationDOM?.pathname?.split('/');

	// location, assetWithIssues, table
	const action = paths?.length >= 3 ? paths[2] : null;
	const filterId = paths?.length >= 4 ? paths[3] : null;

	console.log('ACTION: ', action, filterId);

	/*
	
		/assets/add
		/assets/all
		/assets/all/issues
		/assets/location/:locationId
		/assets/table/:tableId
		/assets/manufacturer/:manufacturerId
		/assets/model/:modelId
		/assets/category/categoryId
		/assets/tags/:tags

		/assets/:id
		/assets/:id/edit
		/assets/:id/:tab
		/assets/:id/:tab/:setting
	*/

	const IS_ALL_ASSETS_VIEW = action === 'all' && filterId !== 'issues';
	const IS_ALL_ASSETS_WITH_ISSUES_VIEW =
		action === 'all' && filterId === 'issues';

	const IS_LOCATION_VIEW = action === 'location' && filterId;
	const IS_TAGS_VIEW = action === 'tags' && filterId;
	const IS_TABLE_VIEW = action === 'table' && filterId;

	/* 
		Filters (Asset)

			query: queryFilter,
			categoryId: categoryFilter,
			locationId: locationFilter || filterId,
			manufacturerId: manufacturerFilter,
			modelId: modelFilter,

		Table settings

			pageSize,
			page,
			sortBy,
			sortOrder,
			
	*/

	const {
		value: items,
		status,
		error,
		execute: refreshAssets,
	} = useAsync(getAssets, {
		silent: true,
		/*defaultPayload: {
			pageSize: null,
			page,
			sortBy,
			sortOrder,
			query: queryFilter,
			categoryId: categoryFilter,
			locationId: locationFilter,
			manufacturerId: manufacturerFilter,
			modelId: modelFilter,

			// all assets
			...(IS_ALL_ASSETS_VIEW
				? {
						savedViewId: 'allAssetsView',
				  }
				: {}),

			// all assets with issues
			...(IS_ALL_ASSETS_WITH_ISSUES_VIEW
				? {
						savedViewId: 'allAssetsWithIssuesView',
						withIssues: true,
				  }
				: {}),

			// location based
			...(action === 'location' ? { locationId: filterId } : {}),

			// table based
			...(action === 'table' ? { tableId: filterId } : {}),
		},*/
		immediate: false,
	});

	const { value: moduleFields } = useAsync(getModuleFields, {
		defaultPayload: { id: ASSETS_MODULE_ID },
	});

	const { execute: onUpdateTableView, status: isUpdatingTableView } = useAsync(
		updateTableView,
		{ immediate: false },
	);

	const { value: locations } = useAsync(getLocations);

	useEffect(() => {
		if (locationFilter && locations?.rows?.length) {
			setSelectedLocation(
				locations?.rows?.find((o) => o.id === locationFilter),
			);
		}
	}, [locationFilter, locations]);

	const {
		value: tableViews,
		status: isLoadingTableViews,
		execute: onRefreshTableViews,
	} = useAsync(getAllTableViews, {
		defaultPayload: { tableTypeName: 'asset' },
	});

	const { value: allTags, execute: onRefreshTags } = useAsync(getTags, {
		immediate: false,
	});

	const { execute: onCreateTableView } = useAsync(createTableView, {
		immediate: false,
	});

	const { execute: onDeleteTableView, status: isDeletingTableView } = useAsync(
		deleteTableView,
		{ immediate: false },
	);

	useEffect(() => {
		if (IS_TAGS_VIEW) {
			onRefreshTags();
		}
	}, [IS_TAGS_VIEW]);

	useEffect(() => {
		refreshAssets(
			{
				pageSize: null,

				// all assets
				...(IS_ALL_ASSETS_VIEW
					? {
							savedViewId: 'allAssetsView',
						}
					: {}),

				// all assets with issues
				...(IS_ALL_ASSETS_WITH_ISSUES_VIEW
					? {
							savedViewId: 'allAssetsWithIssuesView',
							withIssues: true,
						}
					: {}),

				...(action === 'location'
					? { locationId: filterId }
					: { locationId: undefined }),
				...(action === 'table'
					? { tableId: filterId }
					: { tableId: undefined }),
				...(locationFilter ? { locationId: locationFilter } : {}),
			},
			null,
			//{ overrideDefault: true }
		);
	}, [
		//sortBy,
		//sortOrder,
		//manufacturerFilter,
		//modelFilter,
		//categoryFilter,
		locationFilter,
		//page,
		action,
		filterId,
	]);

	const prevQueryFilter = usePrevious(queryFilter);
	useEffect(() => {
		if (queryFilter || prevQueryFilter) {
			const delayDebounceFn = setTimeout(
				() => {
					refreshAssets(
						{
							page,
							sortBy,
							sortOrder,
							query: queryFilter,
							categoryId: categoryFilter,
							locationId: locationFilter || filterId,
							manufacturerId: manufacturerFilter,
							modelId: modelFilter,
						},
						null,
						{ silent: true },
					);
				},
				queryFilter ? 600 : 0,
			);

			return () => clearTimeout(delayDebounceFn);
		}
	}, [queryFilter]);

	useEffect(() => {
		if (action === 'new') {
			setOpen({
				onClose: () => {
					navigate('/assets');
				},
			});
		}
	}, [action]);

	/*
		Additional Locations Items
	*/

	const location = locations?.rows?.find(
		(location) => location.id === (locationFilter || filterId),
	);
	const locationName = location?.name;

	const locationChildren = locations?.rows?.filter(
		(location) => location.parent_id === (locationFilter || filterId),
	);

	const locationParentId = locations?.rows?.find(
		(location) => location.id === (locationFilter || filterId),
	)?.parent_id;

	const activeBanner = location?.health
		? HEALTH_BANNERS[location?.health]
		: null;

	const BADGE_DATA = {
		operational: { label: 'Operational', value: 'success' },
		maintenance: { label: 'Maintenance', value: 'info' },
		caution: { label: 'Caution', value: 'caution' },
		down: { label: 'Down', value: 'error' },
		offline: { label: 'Offline', value: 'default' },
	};

	const sortDateFn = (rowA, rowB, _columnId) => {
		const a = rowA?.original[_columnId]?.date;
		const b = rowB?.original[_columnId]?.date;

		const dateA = a ? new Date(a) : null;
		const dateB = b ? new Date(b) : null;

		if (!dateA && !dateB) return 0;

		if (!dateA) return -1; // a is null, push to end
		if (!dateB) return 1; // b is null, push to end

		return dateB - dateA;
	};

	const sortStatusFn = (rowA, rowB, _columnId) => {
		const statusA = BADGE_DATA[rowA?.original?.health || 'operational']?.value;
		const statusB = BADGE_DATA[rowB?.original?.health || 'operational']?.value;

		const statusOrder = ['success', 'caution', 'info', 'offline', 'error'];
		return statusOrder.indexOf(statusA) - statusOrder.indexOf(statusB);
	};

	const sortModelFn = (rowA, rowB, _columnId) => {
		const a = rowA.original.model.name;
		const b = rowB.original.model.name;
		return a.toString().localeCompare(b.toString());
	};

	const customColumns = [
		{
			header: 'Next PM',
			columnName: 'Next PM',
			enableGrouping: false,
			accessorKey: 'nextPM',
			enableColumnFilter: false,
			cellType: (info) => {
				const row = info?.row?.original;
				const date = moment(row?.nextPM?.date);
				const isOverdue = moment().isAfter(date);

				return isOverdue ? 'error' : null;
			},
			cell: (info) => {
				const row = info?.row?.original;
				const date = moment(row?.nextPM?.date);
				const isOverdue = moment().isAfter(date);

				return row?.nextPM?.date ? (
					isOverdue ? (
						<Badge isBeta status={'error'} size="xxs">{`Overdue: ${date?.format(
							'MM/DD/YYYY',
						)}`}</Badge>
					) : (
						date.format('MM/DD/YYYY')
					)
				) : (
					''
				);
			},
			column: {
				removable: true,
			},
			hiddenByDefault: true,
			sortingFn: sortDateFn,
		},
	];

	const COLUMNS = [
		{
			header: 'Manufacturer',
			accessorKey: 'manufacturer',
			enablePinning: true,
			minSize: 180,
			getGroupingValue: (info) => {
				return info?.manufacturer?.name;
			},
			cell: (info) => {
				const asset = info?.row?.original;
				const manufacturer =
					asset?.manufacturer?.name || asset?.manufacturerName;
				return manufacturer;
			},
			accessorFn: (op) => op.manufacturer?.name,
			enableColumnFilter: true,
			filterFn: 'multiselect',
			enableHiding: false,
			hoverType: 'selectable',
			meta: {
				filterVariant: 'multiselect',
			},
			/*filter: [
				{
					key: 'manufacturerId',
					placeholder: 'Select a manufacturer',
					options: items?.uniqueManufacturers || [],
				},
				{
					key: 'modelId',
					placeholder: 'Select a model',
					options: items?.uniqueModels || [],
				},
			],*/
		},
		{
			header: 'Model',
			accessorKey: 'model',
			accessorFn: (op) => op.model?.name,
			enablePinning: true,
			enableHiding: false,
			minSize: 180,
			//hoverType: 'chevron-link',
			getGroupingValue: (info) => {
				return info?.model?.name;
			},
			cell: (info) => {
				const asset = info?.row?.original;
				const model = asset?.model?.name || asset?.modelName;
				return model;
			},
			sortingFn: sortModelFn,
			enableColumnFilter: true,
			filterFn: 'multiselect',
			hoverType: 'selectable',

			meta: {
				filterVariant: 'multiselect',
			},
			filter: [
				{
					key: 'manufacturerId',
					placeholder: 'Select a manufacturer',
					options: items?.uniqueManufacturers || [],
				},
				{
					key: 'modelId',
					placeholder: 'Select a model',
					options: items?.uniqueModels || [],
				},
			],
		},
		{
			header: 'Serial Number',
			accessorKey: 'serialNumber',
			enableHiding: false,
			enableGrouping: false,
			enableColumnFilter: false,
			hoverType: 'selectable',

			cell: (info) => {
				return info?.getValue() || '';
			},
			enableSorting: true,
		},
		{
			header: 'Status',
			footer: (props) => props.column.id,
			accessorKey: 'status',
			accessorFn: (op) => BADGE_DATA[op?.health || 'operational']?.label,
			cellType: (info) => {
				const badge = BADGE_DATA[info?.row?.original?.health || 'operational'];
				return badge?.value;
			},
			cell: (info) => {
				const badge = BADGE_DATA[info?.row?.original?.health || 'operational'];

				return info?.row?.original?.health ? (
					<Badge isBeta status={badge?.value} size="xxs">
						{badge?.label}
					</Badge>
				) : null;
			},
			//enableSorting: false,
			filterFn: 'multiselect',
			meta: {
				filterVariant: 'multiselect',
			},
			column: {
				removable: true,
			},
			sortingFn: sortStatusFn,
			getGroupingValue: (info) => {
				return BADGE_DATA[info?.health || 'operational']?.label;
			},
		},
		{
			header: 'Category',
			accessorKey: 'category.name',
			id: 'category',
			cell: (info) => {
				return info?.getValue() || '';
			},
			enableFiltering: false,
			filter: {
				key: 'categoryId',
				placeholder: 'Select a category',
				//options: items?.uniqueCategories || [],
			},
			sortDescFirst: false,
			column: {
				removable: true,
			},
			filterFn: 'multiselect',
			meta: {
				filterVariant: 'multiselect',
			},
		},
		{
			header: 'Tags',
			accessorKey: 'tags',
			enableSorting: false,
			enableGrouping: false,
			enableColumnFilter: true,
			filterFn: 'multiselect',
			meta: {
				filterVariant: 'multiselect',
			},
			id: 'tags',
			cell: (info) => {
				//return null; //info?.getValue() || '';
				return (
					info?.getValue()?.map((tag) => (
						<Badge
							isBeta
							size="xxs"
							className={classNames(
								TAG_COLOR_OPTIONS.find((color) => color?.key === tag?.color)
									?.value,
								'mr-1.5 mt-1.5',
							)}
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								navigate(`/assets/tags/${tag?.id}`);
							}}
						>
							{tag?.name}
						</Badge>
					)) || null
				);
			},
			enableFiltering: false,
			filter: {
				key: 'tagIds',
				placeholder: 'Select a tag',
				options: items?.uniqueTags || [],
			},
			sortDescFirst: false,
			column: {
				removable: true,
			},
		},

		{
			header: 'Scipods Tag',
			accessorKey: 'tag',
			enableGrouping: false,

			cell: (info) => {
				return info?.getValue() || '';
			},
			column: {
				removable: true,
			},
			enableColumnFilter: false,
			hiddenByDefault: true,
		},
		{
			header: 'Location',
			accessorKey: 'location.name',
			id: 'location',
			cell: (info) => {
				return info?.getValue() || '';
			},
			enableSorting: true,
			sortDescFirst: false,
			enableFiltering: false,
			filter: {
				key: 'locationId',
				placeholder: 'Select a location',
				options: items?.uniqueLocations || [],
			},
			filterFn: 'multiselect',
			meta: {
				filterVariant: 'multiselect',
			},
			column: {
				removable: true,
			},
		},
		...customColumns,
		...(moduleFields
			? moduleFields
					?.filter(
						(attr) =>
							[
								'manufacturer',
								'model',
								'serialNumber',
								'location',
								'category',
								'tags',
							].indexOf(attr?.key) < 0,
					)
					.map((attr) => {
						const FILTER_FNS = {
							numeric: 'range',
							boolean: 'boolean',
							textarea: 'text',
							text: 'text',
							vendor: 'multiselect',
							category: 'multiselect',
							location: 'multiselect',
						};

						const ALLOW_GROUPING_TYPES = [
							'boolean',
							'vendor',
							'category',
							'location',
						];

						return {
							header: attr?.label,
							accessorKey: attr?.id,
							accessorFn: (op) => {
								const attribute = op?.custom_attributes?.find(
									(o) => o.attribute_field_id === attr?.id,
								);
								const CAP = attr?.type === 'boolean';

								const ATTRIBUTE_DATA_FIELD = {
									vendor: ['value.companyName', 'value.firstName'],
									category: 'value.name',
									location: 'value.name',
								};

								return CAP
									? capitalizeFirstLetter(attribute?.value || '')
									: objectAccess(ATTRIBUTE_DATA_FIELD[attr?.type], attribute);
							},
							cell: (info) => {
								const attribute = info.row?.original?.custom_attributes?.find(
									(o) => o.attribute_field_id === attr?.id,
								);

								const CAP = attr?.type === 'boolean';

								const ATTRIBUTE_DATA_FIELD = {
									vendor: ['value.companyName', 'value.firstName'],
									category: 'value.name',
									location: 'value.name',
								};

								return CAP
									? capitalizeFirstLetter(attribute?.value || '')
									: objectAccess(ATTRIBUTE_DATA_FIELD[attr?.type], attribute);
							},
							sortingFn: 'alphanumeric',
							filterFn: FILTER_FNS[attr?.type],
							meta: {
								filterVariant: FILTER_FNS[attr?.type],
							},
							enableSorting: true,
							enableGrouping: ALLOW_GROUPING_TYPES.includes(attr?.type),
							column: {
								removable: true,
							},
							getGroupingValue: (info) => {
								const attribute = info?.custom_attributes?.find(
									(o) => o.attribute_field_id === attr?.id,
								);
								return attribute?.value || '';
							},
							hiddenByDefault: true,
						};
					})
			: []),
	];

	const applyCustomOrder = (arr, desired) => {
		if (!desired) {
			return arr;
		}
		let firstCol = arr.find((o) => o.accessorKey === desired);
		if (!firstCol) {
			return arr;
		}
		return [firstCol, ...arr.filter((o) => o.accessorKey !== desired)];
	};

	let tabs = tableViews || [];

	if (tabs.length) {
		let [allAssetsTab, ...restOfTabs] = tabs;
		tabs = [
			allAssetsTab,
			{ name: 'Open Issues', key: 'issues' },
			...restOfTabs?.sort((a, b) => a.name.localeCompare(b.name)),
		];
	}

	if (isLoadingSettings) {
		return (
			<MainContent
				banners={activeBanner ? [{ ...activeBanner, size: 'xs' }] : []}
				maxWidth={getSettings('assetsTableExpanded') ? 'w-full' : undefined}
			>
				<LoadingSkeleton />
			</MainContent>
		);
	}

	console.log('items: ', items);

	const IS_CUSTOM_TABLE = items?.tableSettings?.name;
	const IS_ISSUES_TABLE = action === 'all' && filterId === 'issues';

	console.log('IS_ISSUES_TABLE: ', IS_ISSUES_TABLE);

	const TABLE_NAME = IS_CUSTOM_TABLE
		? items?.tableSettings?.name
		: IS_ISSUES_TABLE
			? 'with Open Tickets'
			: null;

	const onRefreshTableViewsWithAssets = () => {
		onRefreshTableViews(undefined, undefined, { silent: true });
		refreshAssets(
			{
				pageSize: null,

				// all assets
				...(IS_ALL_ASSETS_VIEW
					? {
							savedViewId: 'allAssetsView',
						}
					: {}),

				// all assets with issues
				...(IS_ALL_ASSETS_WITH_ISSUES_VIEW
					? {
							savedViewId: 'allAssetsWithIssuesView',
							withIssues: true,
						}
					: {}),

				...(action === 'location'
					? { locationId: filterId }
					: { locationId: undefined }),
				...(action === 'table'
					? { tableId: filterId }
					: { tableId: undefined }),
			},
			null,
			{ silent: true },
		);
	};

	console.log('TABLE ANME: ', TABLE_NAME);

	const TABLE_ID = items?.tableSettings?.id;

	return (
		<>
			<MainContent maxWidth={'w-full'} fullWidth={true}>
				<div className="hidden sm:block">
					<PageHeading
						gap={'sm'}
						actions={[
							{
								name: 'Create Asset',
								onClick: () => setOpen(),
								/*navigate(
								`/assets/new${
									locationFilter ? `?location=${locationFilter}` : ''
								}`
							),*/
							},
						]}
						divider={false}
					>
						{/* BETA Page Heading
				
				<div class="max-lg:hidden mb-4 lg:mb-8">
					<a
						class="inline-flex items-center gap-2 text-sm/6 text-zinc-500 font-normal"
						data-headlessui-state=""
						href="/dashboard"
					>
						<ChevronLeftIcon className="size-4" />
						Dashboard
					</a>
				</div>
			*/}
						<div>
							<div className="flex items-center gap-4">
								<h1 className="text-2xl/4 font-semibold text-zinc-950 sm:text-xl/8 flex items-center">
									All Assets{' '}
									{TABLE_NAME ? (
										<>
											{IS_CUSTOM_TABLE ? (
												<span className="text-gray-300 font-normal mx-3">
													/
												</span>
											) : null}
											{`${TABLE_NAME}`}
										</>
									) : (
										''
									)}
									{IS_CUSTOM_TABLE ? (
										<Menu
											as="div"
											className={classNames('relative inline-block text-left')}
										>
											<div>
												<Menu.Button className="flex items-center rounded-full text-gray-400 hover:text-gray-600">
													<span className="sr-only">Open options</span>
													<Cog6ToothIcon className="w-5 h-5 text-gray-500 ml-3 cursor-pointer" />
												</Menu.Button>
											</div>

											<Transition
												as={Fragment}
												enter="transition ease-out duration-100"
												enterFrom="transform opacity-0 scale-95"
												enterTo="transform opacity-100 scale-100"
												leave="transition ease-in duration-75"
												leaveFrom="transform opacity-100 scale-100"
												leaveTo="transform opacity-0 scale-95"
											>
												<Menu.Items className="absolute left-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
													<div className="py-1">
														<Menu.Item>
															{({ active }) => (
																<div
																	onClick={() => {
																		setRenameTableModalOpen({
																			name: items?.tableSettings?.name,
																		});
																	}}
																	className={classNames(
																		'text-gray-700 block px-4 py-2 text-sm font-normal cursor-pointer',
																		//danger ? 'text-red-700 hover:bg-red-50' : '',
																	)}
																>
																	Rename Table
																</div>
															)}
														</Menu.Item>
														<Menu.Item>
															{({ active }) => (
																<div
																	onClick={() =>
																		openDeleteModal({
																			title: `Are you sure you want to delete this table?`,
																			confirmText: 'Delete Table',
																			onConfirm: () =>
																				onDeleteTableView(
																					{ id: TABLE_ID },
																					() => {
																						onRefreshTableViews();
																						navigate('/assets/all');
																						closeDeleteModal();
																					},
																				),
																		})
																	}
																	className={classNames(
																		' block px-4 py-2 text-sm font-normal cursor-pointer text-red-700 hover:text-red-900 hover:bg-red-50',
																	)}
																>
																	Delete Table
																</div>
															)}
														</Menu.Item>
													</div>
												</Menu.Items>
											</Transition>
										</Menu>
									) : null}
								</h1>
							</div>
						</div>
					</PageHeading>
				</div>
				<div className="sm:hidden">
					{' '}
					<PageHeading>
						<h1 className="text-2xl/4 font-semibold text-zinc-950 sm:text-xl/8 flex items-center mt-4">
							{action === 'issues'
								? 'Assets with Issues'
								: locationName
									? locationName
									: items?.tableSettings?.name || 'All Assets'}
						</h1>
					</PageHeading>
				</div>

				{isLoadingTableViews === 'pending' ? (
					<LoadingSkeleton />
				) : (
					<>
						<div>
							<div>
								<div className="grid grid-cols-1 sm:hidden">
									{/* Use an "onChange" listener to redirect the user to the selected tab URL. */}
									<select
										defaultValue={tabs.find((tab) => tab?.current)?.name}
										aria-label="Select a tab"
										className="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-2 pl-3 pr-8 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
									>
										{tabs.map((tab) => (
											<option key={tab.name}>{tab.name}</option>
										))}
									</select>
									<ChevronDownIcon
										aria-hidden="true"
										className="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end fill-gray-500"
									/>
								</div>
								<div className="sm:block">
									<div className="border-b border-gray-200">
										<nav
											aria-label="Tabs"
											className="-mb-px flex space-x-8 overflow-x-scroll"
										>
											{tabs.map((tab, i) => {
												const current = IS_TABLE_VIEW
													? tab?.id === filterId
													: filterId === 'issues'
														? tab?.key === 'issues'
														: action === tab?.key;

												const count = tab?.count || tab?.assets?.length;

												return (
													<a
														key={tab.name}
														onClick={() =>
															navigate(
																tab?.id
																	? `/assets/table/${tab.id}`
																	: tab?.key === 'issues'
																		? '/assets/all/issues'
																		: '/assets/all',
															)
														}
														aria-current={current ? 'page' : undefined}
														className={classNames(
															current
																? 'border-indigo-500 text-indigo-600'
																: 'border-transparent text-gray-500 hover:border-gray-200 hover:text-gray-700',
															'flex whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium cursor-pointer',
														)}
													>
														{tab.name}
														{count && (
															<span
																className={classNames(
																	current
																		? 'bg-indigo-100 text-indigo-600'
																		: 'bg-gray-100 text-gray-900',
																	'ml-3 hidden rounded-full px-2.5 py-0.5 text-xs font-medium md:inline-block',
																)}
															>
																{count}
															</span>
														)}
													</a>
												);
											})}

											<div
												key={'add-view'}
												className={classNames(
													'border-transparent text-gray-500 hover:text-gray-700',
													'group inline-flex items-center border-b-2 px-1 py-4 text-sm font-medium cursor-pointer',
												)}
												onClick={() => {
													onCreateTableView(
														{
															name: 'New list',
															tableTypeName: 'asset',
														},
														(response) => {
															onRefreshTableViews();
															navigate(`/assets/table/${response?.id}`);
														},
														{ silent: true },
													);
												}}
											>
												<PlusIcon
													aria-hidden="true"
													className={classNames(
														'text-gray-400 group-hover:text-gray-500',
														'-ml-0.5 mr-2 size-5',
													)}
												/>
											</div>
										</nav>
									</div>
								</div>
							</div>
						</div>

						<div className="hidden md:block">
							{status === 'pending' ? (
								<LoadingSkeleton />
							) : (
								<NewDataTableBeta
									columns={COLUMNS}
									data={items?.rows?.length ? items : { rows: [] }}
									onRow={(row) => navigate(`/assets/${row?.original?.id}`)}
									tableSettings={items?.tableSettings}
									moduleId={ASSETS_MODULE_ID}
									namePrefix={'asset'} // assets only
									includeExcludeAssets // assets only
									onRefreshTableViews={onRefreshTableViewsWithAssets}
									savedViewId={
										IS_ALL_ASSETS_VIEW
											? 'allAssetsView'
											: IS_ALL_ASSETS_WITH_ISSUES_VIEW
												? 'allAssetsWithIssuesView'
												: undefined
									}
									defaultFilters={[
										...(IS_LOCATION_VIEW
											? [{ id: 'location', value: [locationName || 'not-set'] }]
											: []),
										...(IS_TAGS_VIEW && allTags
											? [
													{
														id: 'tags',
														value: allTags
															?.filter((o) =>
																IS_TAGS_VIEW?.split(',')?.includes(o.id),
															)
															?.map((o) => o.name),
													},
												]
											: []),
									]}
								/>
							)}
						</div>
						<div className="grid grid-cols-12 gap-x-8">
							<div className="col-span-12">
								{/*<DataTable
								pinColumn={[DESIRED_MAIN_COLUMN_KEY]}
								columns={applyCustomOrder(COLUMNS, DESIRED_MAIN_COLUMN_KEY)}
								data={items?.rows?.length ? items : { rows: [] }}
								onRow={(item) => navigate(`/assets/${item?.original?.id}`)}
								rowActions={{
									name: 'View',
									onClick: (row) =>
										navigate(`/assets/${row?.original?.id}`),
								}}
								usePagination={false}
								columnManagementKey="assetsMainColumns"
								onExport={() => {}}
								exportFileName={`assets-${moment().format('MM-DD-YYYY')}`}
								useExport={false}
								hasCheckboxes={false}
								manualSorting={false}
								settingsMenuItems={[
									{
										name: getSettings('assetsTableExpanded')
											? 'Collapse Table'
											: 'Expand Table',
										onClick: () =>
											updateSettings(
												'user',
												'assetsTableExpanded',
												!getSettings('assetsTableExpanded')
											),
									},
								]}
							/>*/}

								{IS_LOCATION_VIEW && locationChildren?.length ? (
									<>
										<h3 class="text-sm font-semibold leading-6 text-gray-700 flex align-center tracking-tight mb-4">
											Locations
										</h3>
										<LocationList locations={locationChildren} />
									</>
								) : null}
							</div>
						</div>
					</>
				)}
			</MainContent>

			<Modal
				title="Rename Table"
				open={isRenameTableModalOpen}
				onClose={() => setRenameTableModalOpen(false)}
			>
				<TemplateField
					name="Table Name"
					autoFocus
					value={isRenameTableModalOpen?.name}
					onChange={(index, name) =>
						setRenameTableModalOpen({ ...isRenameTableModalOpen, name })
					}
				/>

				<div className="flex justify-end mt-3">
					<Button
						disabled={!isRenameTableModalOpen?.name}
						onClick={() => {
							onUpdateTableView(
								{
									id: TABLE_ID,
									name: isRenameTableModalOpen?.name,
								},
								() => {
									setRenameTableModalOpen(false);
									onRefreshTableViewsWithAssets();
								},
								{ silent: true },
							);
						}}
					>
						Rename Table
					</Button>
				</div>
			</Modal>
		</>
	);
};

export default AllList;
